import React from "react";
import { Button } from "@pd/pd-ui-kit";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";

import IconApply from "~/components/Icons/icon-check-mark";
import IconCross from "~/components/Icons/icon-cross";
import IconEdit from "~/components/Icons/icon-default";
import { useSetLocationData } from "~/hooks/locationData";
import {
  StatusSetResponseToDealer,
  useDealerFixViolationMutation,
  useDeleteViolationMutation,
  useGiveResponseToDealerMutation,
  useRestoreViolationMutation,
  useUnDeleteViolationMutation,
  ViolationStatus,
} from "~/schemas/generated/graphql";
import { usePermissions } from "~/shared/context/PermissionContext";

import s from "~/pages/Violations/ViolationDescription/violationDescription.less";

const Controls = ({
  page,
  refetch,
  personalId,
  status,
  deadlineCounter,
  dealerWroteLastComment,
}) => {
  const { t } = useTranslation();
  const history = useHistory();
  const { hasPermission, loading: loadingPermissions } = usePermissions();
  const setData = useSetLocationData();
  const setDataReplace = useSetLocationData(true);
  const statusFixed = status === ViolationStatus.Fixed;
  const statusToFix = status === ViolationStatus.ToFix;
  const statusDealerResp = status === ViolationStatus.GotDealerResponse;
  const statusOwnerResp = status === ViolationStatus.HasComments;
  const statusDeleted = status === ViolationStatus.Deleted;
  const statusRejected = status === ViolationStatus.Reject;
  const dealerCanReply = statusOwnerResp || statusToFix;
  // const adminCanReply = dealerWroteLastComment !== undefined && !dealerWroteLastComment && statusDealerResp;
  const adminCanReply = statusDealerResp;

  const timeIsOut = deadlineCounter <= 0;

  const [setChangeViolationStatus] = useGiveResponseToDealerMutation({
    onCompleted: () => {
      refetch();
    },
  });

  const [setDealerFix] = useDealerFixViolationMutation({
    onCompleted: () => {
      refetch();
    },
  });

  const [deleteViolation] = useDeleteViolationMutation({
    onCompleted: () => {
      refetch();
    },
  });

  const [restoreViolation] = useRestoreViolationMutation({
    onCompleted: () => {
      refetch();
    },
  });

  const [unDeleteViolation] = useUnDeleteViolationMutation({
    onCompleted: () => {
      refetch();
    },
  });

  const changeStatus = (newStatus) => {
    setChangeViolationStatus({
      variables: {
        id: personalId,
        status: newStatus,
      },
    });
  };

  const dealerAnswerHandler = (id) => {
    setDealerFix({
      variables: {
        id: id,
      },
    });
  };

  const deleteAndRestoreHandler = (mutation) => {
    mutation({
      variables: {
        id: personalId,
      },
    });
  };

  const openSanctionsModal = () => {
    setDataReplace("openModal", "sanctions");
  };

  const openEditPage = () => {
    history.push(`/violations/violation_recording/${personalId}`);
    page && setData("page", page);
  };

  return (
    <div className={s.controls}>
      {!statusDeleted ? (
        <>
          <div className={s.controls_row}>
            {!statusFixed &&
              (hasPermission("violations.delete") ||
                hasPermission("violations.reject") ||
                hasPermission("violations.giveResponseToDealer")) &&
              !statusRejected && (
                <>
                  {adminCanReply &&
                    hasPermission("violations.giveResponseToDealer") && (
                      <Button
                        color="success"
                        variant={'outlined'}
                        size="small"
                        icon={<IconApply fill={'#4ebf54'} />}
                        onClick={() =>
                          changeStatus(StatusSetResponseToDealer.HasComments)
                        }
                      >
                        {t("ui.replyFix")}
                      </Button>
                    )}
                  {hasPermission("violations.reject") && (
                    <>
                      <Button
                        color="success"
                        size="small"
                        icon={<IconApply fill={'#fff'} />}
                        onClick={() =>
                          changeStatus(StatusSetResponseToDealer.Fixed)
                        }
                      >
                        {t("ui.apply")}
                      </Button>
                      <Button
                        color="danger"
                        size="small"
                        icon={<IconCross fill={'#fff'} />}
                        onClick={openSanctionsModal}
                      >
                        {t("ui.dismiss")}
                      </Button>
                    </>
                  )}
                  {hasPermission("violations.delete") && (
                    <Button
                      variant={'outlined'}
                      size="small"
                      icon={<IconCross fill={'#ff9e1b'} />}
                      onClick={() => deleteAndRestoreHandler(deleteViolation)}
                    >
                      {t("ui.delete")}
                    </Button>
                  )}
                </>
              )}
            {(statusFixed || statusRejected) &&
              hasPermission("violations.edit") && (
                <Button
                  size="small"
                  onClick={() => deleteAndRestoreHandler(unDeleteViolation)}
                >
                  {t("ui.unDelete")}
                </Button>
              )}
          </div>
          <div className={s.controls_row}>
            {!statusFixed &&
              hasPermission("violations.edit") &&
              !statusRejected && (
                <Button size="small" icon={<IconEdit />} onClick={openEditPage}>
                  {t("ui.edit")}
                </Button>
              )}
            {hasPermission("violations.dealerFix") && dealerCanReply && (
              <Button
                color="success"
                variant="outlined"
                size="small"
                icon={<IconApply fill={'#4ebf54'} />}
                onClick={() => dealerAnswerHandler(personalId)}
              >
                {t("ui.replyDealer")}
              </Button>
            )}
            {hasPermission("violations.delete") &&
              (statusFixed || statusRejected) && (
                <Button
                  color="danger"
                  variant="outlined"
                  size="small"
                  icon={<IconCross fill={'#ff9e1b'} />}
                  onClick={() => deleteAndRestoreHandler(deleteViolation)}
                >
                  {t("ui.delete")}
                </Button>
              )}
          </div>
        </>
      ) : (
        hasPermission("violations.unDelete") && (
          <>
            <div className={s.controls_row}></div>
            <div className={s.controls_row}>
              <Button
                color="success"
                variant="outlined"
                size="small"
                icon={<IconApply fill={'#4ebf54'} />}
                onClick={() => deleteAndRestoreHandler(restoreViolation)}
              >
                {t("ui.restore")}
              </Button>
            </div>
          </>
        )
      )}
    </div>
  );
};

export default Controls;
